/**
 * SprintContext — Unified sprint selection state for all project-scoped pages.
 *
 * Single query fetches ALL sprints for the project (no status filter), groups
 * them by status, and resolves a selected sprint from ?sprint= URL param or
 * auto-resolution: building > most recent planning (7d) > most recent completed/failed.
 *
 * selectSprint(id) updates the URL search param, which triggers re-resolution.
 */

import { createContext, useContext, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sprintApi, type Sprint } from '../api/sprint';
import { useProject } from './ProjectContext';

interface SprintsByStatus {
  building: Sprint[];
  planning: Sprint[];
  completed: Sprint[];
  failed: Sprint[];
}

interface SprintContextValue {
  /** All sprints for the current project */
  allSprints: Sprint[];
  /** Sprints grouped by status */
  sprintsByStatus: SprintsByStatus;
  /** The currently selected/resolved sprint (may be null if no sprints exist) */
  selectedSprint: Sprint | null;
  /** Select a sprint by ID — updates URL search param */
  selectSprint: (id: string) => void;
  /** Convenience: true if selectedSprint.status === 'building' */
  isBuilding: boolean;
  /** Convenience: true if selectedSprint.status === 'planning' */
  isPlanning: boolean;
  /** Convenience: true if selectedSprint.status === 'completed' */
  isCompleted: boolean;
  /** Convenience: true if selectedSprint.status === 'failed' */
  isFailed: boolean;
  /** True while the sprint list is still loading */
  isLoading: boolean;
}

const SprintContext = createContext<SprintContextValue | null>(null);

const EMPTY_GROUPS: SprintsByStatus = { building: [], planning: [], completed: [], failed: [] };
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

export function SprintProvider({ children }: { children: React.ReactNode }) {
  const { project } = useProject();
  const projectId = project?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const sprintParam = searchParams.get('sprint');

  // Single query: all sprints for this project
  const { data, isLoading } = useQuery({
    queryKey: ['sprints', 'all', projectId],
    queryFn: () => sprintApi.list(projectId!),
    enabled: !!projectId,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  // Memoize the fallback — `data?.sprints ?? []` without memoization creates a
  // fresh array on every render while data is undefined, invalidating
  // downstream useMemo deps and rebuilding the context value each time.
  const allSprints = useMemo(() => data?.sprints ?? [], [data]);

  // Group by status
  const sprintsByStatus = useMemo<SprintsByStatus>(() => {
    if (allSprints.length === 0) return EMPTY_GROUPS;
    const groups: SprintsByStatus = { building: [], planning: [], completed: [], failed: [] };
    for (const s of allSprints) {
      if (s.status in groups) {
        groups[s.status as keyof SprintsByStatus].push(s);
      }
    }
    // Sort planning by createdAt desc, completed/failed by completedAt/failedAt desc
    groups.planning.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    groups.completed.sort((a, b) =>
      new Date(b.completedAt || b.createdAt).getTime() - new Date(a.completedAt || a.createdAt).getTime()
    );
    groups.failed.sort((a, b) =>
      new Date(b.failedAt || b.createdAt).getTime() - new Date(a.failedAt || a.createdAt).getTime()
    );
    return groups;
  }, [allSprints]);

  // Resolve selected sprint
  const selectedSprint = useMemo<Sprint | null>(() => {
    if (allSprints.length === 0) return null;

    // 1. URL param — find exact match
    if (sprintParam) {
      const found = allSprints.find(s => s.id === sprintParam);
      if (found) return found;
    }

    // 2. Auto-resolve: first building sprint
    if (sprintsByStatus.building.length > 0) return sprintsByStatus.building[0];

    // 3. Most recent planning sprint within 7 days
    const now = Date.now();
    const recentPlanning = sprintsByStatus.planning.find(
      s => now - new Date(s.createdAt).getTime() < SEVEN_DAYS
    );
    if (recentPlanning) return recentPlanning;

    // 4. Most recent completed or failed
    const recentTerminal = [
      ...sprintsByStatus.completed,
      ...sprintsByStatus.failed,
    ].sort((a, b) =>
      new Date(b.completedAt || b.failedAt || b.createdAt).getTime() -
      new Date(a.completedAt || a.failedAt || a.createdAt).getTime()
    );
    if (recentTerminal.length > 0) return recentTerminal[0];

    // 5. Fallback to first sprint in list
    return allSprints[0] ?? null;
  }, [allSprints, sprintParam, sprintsByStatus]);

  // selectSprint updates URL param, preserving other params (tab, etc.)
  const selectSprint = useCallback((id: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('sprint', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const value = useMemo<SprintContextValue>(() => ({
    allSprints,
    sprintsByStatus,
    selectedSprint,
    selectSprint,
    isBuilding: selectedSprint?.status === 'building',
    isPlanning: selectedSprint?.status === 'planning',
    isCompleted: selectedSprint?.status === 'completed',
    isFailed: selectedSprint?.status === 'failed',
    isLoading,
  }), [allSprints, sprintsByStatus, selectedSprint, selectSprint, isLoading]);

  return (
    <SprintContext.Provider value={value}>
      {children}
    </SprintContext.Provider>
  );
}

export function useSprint(): SprintContextValue {
  const ctx = useContext(SprintContext);
  if (!ctx) throw new Error('useSprint must be used within a SprintProvider');
  return ctx;
}
